import React, { FunctionComponent } from 'react';
import { faCopy, faBan, faPen, faCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import { faLocationDot } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import PetTypeTags from 'components/UI/V3/PetTypeTags';
import ThreeDotsDropdown from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdown';
import { Service } from 'model/Service';
import styles from './TransportationServiceRow.module.scss';
import { MarketplaceTax } from '@spike/marketplace-model';
import { ThreeDotsDropdownOption } from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdownOption';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';

interface TransportationServiceRowProps {
    service: Service;
    isLast: boolean;
    isMobile: boolean;
    onEdit?: (service: Service) => void;
    onDuplicate?: (service: Service) => void;
    onDeactivate?: (service: Service) => void;
    onDelete?: (service: Service) => void;
}

export const TransportationServiceRow: FunctionComponent<TransportationServiceRowProps> = ({
    service,
    isLast,
    isMobile,
    onEdit,
    onDuplicate,
    onDeactivate
}) => {
    const { getOrganizationMarketplace } = useOrganizationMarketplaces();
    const petTypes = service.petTypes;
    const isActive = service.status.id === 1;
    const marketplaceId = service.marketplaceId ?? service.marketplaces[0]?.marketplaceId;
    const marketplaceName = marketplaceId != null ? getOrganizationMarketplace(marketplaceId)?.name : undefined;

    const staffCount = service.staff?.length || 0;
    const staffDisplay = staffCount === 1 ? '1 Member' : `${staffCount} Members`;

    const formatPrice = (price?: number) => {
        if (!price) return '$0.00';
        return `$${price.toFixed(2)}`;
    };

    const getTransportationPrices = () => {
        const firstPrice = service.pricing.fixedPrice?.[0];
        if (!firstPrice?.transportationPrice) {
            return { pickupPrice: 0, dropoffPrice: 0 };
        }
        return {
            pickupPrice: firstPrice.transportationPrice.pickUpPrice,
            dropoffPrice: firstPrice.transportationPrice.dropOffPrice
        };
    };

    const prices = getTransportationPrices();

    const getTaxesDisplay = (taxes: Array<MarketplaceTax>) => {
        if (taxes.length === 0) return 'No Taxable';
        return taxes.length === 1 ? '1 tax' : `${taxes.length} taxes`;
    };

    const dropdownOptions: Array<ThreeDotsDropdownOption> = [
        {
            id: 'edit',
            label: 'Edit',
            icon: faPen,
            onClick: () => onEdit?.(service)
        },
        {
            id: 'duplicate',
            label: 'Duplicate',
            icon: faCopy,
            onClick: () => onDuplicate?.(service)
        },
        {
            id: 'deactivate',
            label: isActive ? 'Deactivate' : 'Activate',
            icon: isActive ? faBan : faCircleCheck,
            onClick: () => onDeactivate?.(service)
        }
    ];

    const petTypesAndLocation = (
        <div className={styles.petAndLocationContainer}>
            <PetTypeTags petTypes={petTypes} />
            {marketplaceName && (
                <div className={styles.marketplaceContainer}>
                    <FontAwesomeIcon
                        icon={faLocationDot}
                        className={styles.marketplaceIcon}
                    />
                    <span className={styles.marketplaceName}>{marketplaceName}</span>
                </div>
            )}
        </div>
    );

    if (isMobile) {
        return (
            <TableRow className={styles.desktopRow}>
                <TableCell className={styles.nameMainCell}>
                    <div
                        className={styles.serviceColor}
                        style={{ backgroundColor: service.color || 'transparent' }}
                    />
                    <div className={styles.nameCell}>
                        <p className={styles.serviceName}>{service.name}</p>
                        {petTypesAndLocation}
                    </div>
                </TableCell>
                <TableCell isActions>
                    <ThreeDotsDropdown
                        options={dropdownOptions}
                        direction={isLast ? 'up' : 'down'}
                    />
                </TableCell>
            </TableRow>
        );
    }

    return (
        <TableRow className={styles.desktopRow}>
            <TableCell className={styles.nameMainCell}>
                <div
                    className={styles.serviceColor}
                    style={{ backgroundColor: service.color || 'transparent' }}
                />
                <div className={styles.nameCell}>
                    <p className={styles.serviceName}>{service.name}</p>
                    {petTypesAndLocation}
                </div>
            </TableCell>

            <TableCell>
                <p>{staffDisplay}</p>
            </TableCell>

            <TableCell>
                <p className={styles.price}>{formatPrice(prices.pickupPrice)}</p>
            </TableCell>

            <TableCell>
                <p className={styles.price}>{formatPrice(prices.dropoffPrice)}</p>
            </TableCell>

            <TableCell>
                <p>{getTaxesDisplay(service.pricing.taxes)}</p>
            </TableCell>

            <TableCell isActions>
                <ThreeDotsDropdown
                    options={dropdownOptions}
                    direction={isLast ? 'up' : 'down'}
                />
            </TableCell>
        </TableRow>
    );
};

export default TransportationServiceRow;
